<div class="notification-list-container" [class.compact]="compact()">

  <!-- Header with actions -->
  @if (showActions()) {
    <div class="notification-header">
      <div class="header-content">
        <h3 class="notification-title">
          <mat-icon>notifications</mat-icon>
          {{ 'shared.notifications.list.title' | translate }}
          @if ((summary$ | async)?.unreadCount; as unreadCount) {
            <span
              [matBadge]="unreadCount"
              [matBadgeHidden]="unreadCount === 0"
              matBadgeColor="warn"
              matBadgeSize="small">
            </span>
          }
        </h3>
        <div class="header-actions">
          <!-- Test button for development -->
          <button
            mat-icon-button
            (click)="createTestNotifications()" (keydown.enter)="createTestNotifications()"
            [matTooltip]="'shared.notifications.list.testTooltip' | translate"
            color="primary">
            <mat-icon>bug_report</mat-icon>
          </button>
          <!-- Mark all as read -->
          <button
            mat-icon-button
            (click)="markAllAsRead()" (keydown.enter)="markAllAsRead()"
            [matTooltip]="'shared.notifications.list.markAllReadTooltip' | translate"
            [disabled]="(summary$ | async)?.unreadCount === 0">
            <mat-icon>done_all</mat-icon>
          </button>
        </div>
      </div>
    </div>
  }

  <!-- Summary chips -->
  @if (summary$ | async; as summary) {
    <div class="notification-summary">
      <mat-chip-set>
        @if (summary.unreadCount > 0) {
          <mat-chip
            color="warn"
            selected>
            {{ 'shared.notifications.list.unreadChip' | translate:{ count: summary.unreadCount } }}
          </mat-chip>
        }
        <mat-chip>{{ 'shared.notifications.list.totalChip' | translate:{ count: summary.totalCount } }}</mat-chip>
      </mat-chip-set>
    </div>
  }

  <!-- Grouped notifications -->
  @if (showGrouping()) {
    <div class="notification-content">
      @if (groupedNotifications$ | async; as grouped) {
        <!-- Today -->
        @if (grouped.today.length > 0) {
          <div class="notification-group">
            <h4 class="group-title">{{ 'shared.notifications.list.todayGroup' | translate }}</h4>
            <div class="notification-items">
              @for (notification of grouped.today | slice:0:maxItems(); track trackByNotificationId($index, notification)) {
                <div
                  class="notification-item"
                  [class.unread]="!notification.read"
                  [class.urgent]="notification.priority === NotificationPriority.URGENT"
                  (click)="onNotificationClick(notification)" (keydown.enter)="onNotificationClick(notification)" tabindex="0">
                  <div class="notification-icon">
                    <mat-icon [color]="getNotificationColor(notification)">
                      {{ getNotificationIcon(notification) }}
                    </mat-icon>
                  </div>
                  <div class="notification-content-area">
                    <div class="notification-header-row">
                      <span class="notification-title">{{ notification.title }}</span>
                      <span class="notification-time">{{ getRelativeTime(notification.createdAt) }}</span>
                    </div>
                    <div class="notification-message">{{ notification.message }}</div>
                    <div class="notification-meta">
                      <mat-chip class="category-chip" [color]="getNotificationColor(notification)">
                        {{ getCategoryLabel(notification.category) }}
                      </mat-chip>
                      @if (notification.priority !== NotificationPriority.NORMAL) {
                        <mat-chip
                          class="priority-chip"
                          [color]="getNotificationColor(notification)">
                          {{ getPriorityLabel(notification.priority) }}
                        </mat-chip>
                      }
                    </div>
                  </div>
                  @if (showActions()) {
                    <div class="notification-actions">
                      @if (notification.actionUrl && notification.actionLabel) {
                        <button
                          mat-icon-button
                          (click)="onActionClick(notification, $event)" (keydown.enter)="onActionClick(notification, $any($event))"
                          [matTooltip]="notification.actionLabel"
                          color="primary">
                          <mat-icon>open_in_new</mat-icon>
                        </button>
                      }
                      <button
                        mat-icon-button
                        [matMenuTriggerFor]="notificationMenu"
                        (click)="$event.stopPropagation()" (keydown.enter)="$any($event).stopPropagation()"
                        [matTooltip]="'shared.notifications.list.moreActionsTooltip' | translate">
                        <mat-icon>more_vert</mat-icon>
                      </button>
                      <mat-menu #notificationMenu="matMenu">
                        @if (!notification.read) {
                          <button
                            mat-menu-item
                            (click)="markAsRead(notification, $event)" (keydown.enter)="markAsRead(notification, $any($event))">
                            <mat-icon>done</mat-icon>
                            <span>{{ 'shared.notifications.list.markReadAction' | translate }}</span>
                          </button>
                        }
                        <button
                          mat-menu-item
                          (click)="removeNotification(notification, $event)" (keydown.enter)="removeNotification(notification, $any($event))">
                          <mat-icon>delete</mat-icon>
                          <span>{{ 'shared.notifications.list.removeAction' | translate }}</span>
                        </button>
                      </mat-menu>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
        <!-- Yesterday -->
        @if (grouped.yesterday.length > 0) {
          <div class="notification-group">
            <h4 class="group-title">{{ 'shared.notifications.list.yesterdayGroup' | translate }}</h4>
            <div class="notification-items">
              @for (notification of grouped.yesterday | slice:0:maxItems(); track trackByNotificationId($index, notification)) {
                <div
                  class="notification-item"
                  [class.unread]="!notification.read"
                  [class.urgent]="notification.priority === NotificationPriority.URGENT"
                  (click)="onNotificationClick(notification)" (keydown.enter)="onNotificationClick(notification)" tabindex="0">
                  <div class="notification-icon">
                    <mat-icon [color]="getNotificationColor(notification)">
                      {{ getNotificationIcon(notification) }}
                    </mat-icon>
                  </div>
                  <div class="notification-content-area">
                    <div class="notification-header-row">
                      <span class="notification-title">{{ notification.title }}</span>
                      <span class="notification-time">{{ getRelativeTime(notification.createdAt) }}</span>
                    </div>
                    <div class="notification-message">{{ notification.message }}</div>
                    <div class="notification-meta">
                      <mat-chip class="category-chip" [color]="getNotificationColor(notification)">
                        {{ getCategoryLabel(notification.category) }}
                      </mat-chip>
                      @if (notification.priority !== NotificationPriority.NORMAL) {
                        <mat-chip
                          class="priority-chip"
                          [color]="getNotificationColor(notification)">
                          {{ getPriorityLabel(notification.priority) }}
                        </mat-chip>
                      }
                    </div>
                  </div>
                  @if (showActions()) {
                    <div class="notification-actions">
                      @if (notification.actionUrl && notification.actionLabel) {
                        <button
                          mat-icon-button
                          (click)="onActionClick(notification, $event)" (keydown.enter)="onActionClick(notification, $any($event))"
                          [matTooltip]="notification.actionLabel"
                          color="primary">
                          <mat-icon>open_in_new</mat-icon>
                        </button>
                      }
                      <button
                        mat-icon-button
                        [matMenuTriggerFor]="notificationMenu2"
                        (click)="$event.stopPropagation()" (keydown.enter)="$any($event).stopPropagation()"
                        [matTooltip]="'shared.notifications.list.moreActionsTooltip' | translate">
                        <mat-icon>more_vert</mat-icon>
                      </button>
                      <mat-menu #notificationMenu2="matMenu">
                        @if (!notification.read) {
                          <button
                            mat-menu-item
                            (click)="markAsRead(notification, $event)" (keydown.enter)="markAsRead(notification, $any($event))">
                            <mat-icon>done</mat-icon>
                            <span>{{ 'shared.notifications.list.markReadAction' | translate }}</span>
                          </button>
                        }
                        <button
                          mat-menu-item
                          (click)="removeNotification(notification, $event)" (keydown.enter)="removeNotification(notification, $any($event))">
                          <mat-icon>delete</mat-icon>
                          <span>{{ 'shared.notifications.list.removeAction' | translate }}</span>
                        </button>
                      </mat-menu>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
        <!-- This Week -->
        @if (grouped.thisWeek.length > 0) {
          <div class="notification-group">
            <h4 class="group-title">{{ 'shared.notifications.list.thisWeekGroup' | translate }}</h4>
            <div class="notification-items">
              @for (notification of grouped.thisWeek | slice:0:maxItems(); track trackByNotificationId($index, notification)) {
                <div
                  class="notification-item"
                  [class.unread]="!notification.read"
                  [class.urgent]="notification.priority === NotificationPriority.URGENT"
                  (click)="onNotificationClick(notification)" (keydown.enter)="onNotificationClick(notification)" tabindex="0">
                  <div class="notification-icon">
                    <mat-icon [color]="getNotificationColor(notification)">
                      {{ getNotificationIcon(notification) }}
                    </mat-icon>
                  </div>
                  <div class="notification-content-area">
                    <div class="notification-header-row">
                      <span class="notification-title">{{ notification.title }}</span>
                      <span class="notification-time">{{ getRelativeTime(notification.createdAt) }}</span>
                    </div>
                    <div class="notification-message">{{ notification.message }}</div>
                    <div class="notification-meta">
                      <mat-chip class="category-chip" [color]="getNotificationColor(notification)">
                        {{ getCategoryLabel(notification.category) }}
                      </mat-chip>
                      @if (notification.priority !== NotificationPriority.NORMAL) {
                        <mat-chip
                          class="priority-chip"
                          [color]="getNotificationColor(notification)">
                          {{ getPriorityLabel(notification.priority) }}
                        </mat-chip>
                      }
                    </div>
                  </div>
                  @if (showActions()) {
                    <div class="notification-actions">
                      @if (notification.actionUrl && notification.actionLabel) {
                        <button
                          mat-icon-button
                          (click)="onActionClick(notification, $event)" (keydown.enter)="onActionClick(notification, $any($event))"
                          [matTooltip]="notification.actionLabel"
                          color="primary">
                          <mat-icon>open_in_new</mat-icon>
                        </button>
                      }
                      <button
                        mat-icon-button
                        [matMenuTriggerFor]="notificationMenu3"
                        (click)="$event.stopPropagation()" (keydown.enter)="$any($event).stopPropagation()"
                        [matTooltip]="'shared.notifications.list.moreActionsTooltip' | translate">
                        <mat-icon>more_vert</mat-icon>
                      </button>
                      <mat-menu #notificationMenu3="matMenu">
                        @if (!notification.read) {
                          <button
                            mat-menu-item
                            (click)="markAsRead(notification, $event)" (keydown.enter)="markAsRead(notification, $any($event))">
                            <mat-icon>done</mat-icon>
                            <span>{{ 'shared.notifications.list.markReadAction' | translate }}</span>
                          </button>
                        }
                        <button
                          mat-menu-item
                          (click)="removeNotification(notification, $event)" (keydown.enter)="removeNotification(notification, $any($event))">
                          <mat-icon>delete</mat-icon>
                          <span>{{ 'shared.notifications.list.removeAction' | translate }}</span>
                        </button>
                      </mat-menu>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
        <!-- Older -->
        @if (grouped.older.length > 0) {
          <div class="notification-group">
            <h4 class="group-title">{{ 'shared.notifications.list.olderGroup' | translate }}</h4>
            <div class="notification-items">
              @for (notification of grouped.older | slice:0:maxItems(); track trackByNotificationId($index, notification)) {
                <div
                  class="notification-item"
                  [class.unread]="!notification.read"
                  [class.urgent]="notification.priority === NotificationPriority.URGENT"
                  (click)="onNotificationClick(notification)" (keydown.enter)="onNotificationClick(notification)" tabindex="0">
                  <div class="notification-icon">
                    <mat-icon [color]="getNotificationColor(notification)">
                      {{ getNotificationIcon(notification) }}
                    </mat-icon>
                  </div>
                  <div class="notification-content-area">
                    <div class="notification-header-row">
                      <span class="notification-title">{{ notification.title }}</span>
                      <span class="notification-time">{{ getRelativeTime(notification.createdAt) }}</span>
                    </div>
                    <div class="notification-message">{{ notification.message }}</div>
                    <div class="notification-meta">
                      <mat-chip class="category-chip" [color]="getNotificationColor(notification)">
                        {{ getCategoryLabel(notification.category) }}
                      </mat-chip>
                      @if (notification.priority !== NotificationPriority.NORMAL) {
                        <mat-chip
                          class="priority-chip"
                          [color]="getNotificationColor(notification)">
                          {{ getPriorityLabel(notification.priority) }}
                        </mat-chip>
                      }
                    </div>
                  </div>
                  @if (showActions()) {
                    <div class="notification-actions">
                      @if (notification.actionUrl && notification.actionLabel) {
                        <button
                          mat-icon-button
                          (click)="onActionClick(notification, $event)" (keydown.enter)="onActionClick(notification, $any($event))"
                          [matTooltip]="notification.actionLabel"
                          color="primary">
                          <mat-icon>open_in_new</mat-icon>
                        </button>
                      }
                      <button
                        mat-icon-button
                        [matMenuTriggerFor]="notificationMenu4"
                        (click)="$event.stopPropagation()" (keydown.enter)="$any($event).stopPropagation()"
                        [matTooltip]="'shared.notifications.list.moreActionsTooltip' | translate">
                        <mat-icon>more_vert</mat-icon>
                      </button>
                      <mat-menu #notificationMenu4="matMenu">
                        @if (!notification.read) {
                          <button
                            mat-menu-item
                            (click)="markAsRead(notification, $event)" (keydown.enter)="markAsRead(notification, $any($event))">
                            <mat-icon>done</mat-icon>
                            <span>{{ 'shared.notifications.list.markReadAction' | translate }}</span>
                          </button>
                        }
                        <button
                          mat-menu-item
                          (click)="removeNotification(notification, $event)" (keydown.enter)="removeNotification(notification, $any($event))">
                          <mat-icon>delete</mat-icon>
                          <span>{{ 'shared.notifications.list.removeAction' | translate }}</span>
                        </button>
                      </mat-menu>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        }
        <!-- Empty state -->
        @if (summary$ | async; as summary) {
          <div class="empty-state">
            @if (summary.totalCount === 0) {
              <div class="empty-content">
                <mat-icon class="empty-icon">notifications_none</mat-icon>
                <h4>{{ 'shared.notifications.list.emptyTitle' | translate }}</h4>
                <p>{{ 'shared.notifications.list.emptyMsg' | translate }}</p>
                <button
                  mat-raised-button
                  color="primary"
                  (click)="createTestNotifications()" (keydown.enter)="createTestNotifications()">
                  {{ 'shared.notifications.list.createTestBtn' | translate }}
                </button>
              </div>
            }
          </div>
        } @else {
          <div class="loading-state">
            <mat-icon class="loading-icon">hourglass_empty</mat-icon>
            <p>{{ 'shared.notifications.list.loadingMsg' | translate }}</p>
          </div>
        }
      }
    </div>
  } @else {
    <div class="notification-items simple">
      @for (notification of (notifications$ | async) | slice:0:maxItems(); track trackByNotificationId($index, notification)) {
        <div
          class="notification-item"
          [class.unread]="!notification.read"
          [class.urgent]="notification.priority === NotificationPriority.URGENT"
          (click)="onNotificationClick(notification)" (keydown.enter)="onNotificationClick(notification)" tabindex="0">
          <div class="notification-icon">
            <mat-icon [color]="getNotificationColor(notification)">
              {{ getNotificationIcon(notification) }}
            </mat-icon>
          </div>
          <div class="notification-content-area">
            <div class="notification-header-row">
              <span class="notification-title">{{ notification.title }}</span>
              <span class="notification-time">{{ getRelativeTime(notification.createdAt) }}</span>
            </div>
            <div class="notification-message">{{ notification.message }}</div>
            <div class="notification-meta">
              <mat-chip class="category-chip" [color]="getNotificationColor(notification)">
                {{ getCategoryLabel(notification.category) }}
              </mat-chip>
              @if (notification.priority !== NotificationPriority.NORMAL) {
                <mat-chip
                  class="priority-chip"
                  [color]="getNotificationColor(notification)">
                  {{ getPriorityLabel(notification.priority) }}
                </mat-chip>
              }
            </div>
          </div>
          @if (showActions()) {
            <div class="notification-actions">
              @if (notification.actionUrl && notification.actionLabel) {
                <button
                  mat-icon-button
                  (click)="onActionClick(notification, $event)" (keydown.enter)="onActionClick(notification, $any($event))"
                  [matTooltip]="notification.actionLabel"
                  color="primary">
                  <mat-icon>open_in_new</mat-icon>
                </button>
              }
              <button
                mat-icon-button
                [matMenuTriggerFor]="notificationMenuSimple"
                (click)="$event.stopPropagation()" (keydown.enter)="$any($event).stopPropagation()"
                [matTooltip]="'shared.notifications.list.moreActionsTooltip' | translate">
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #notificationMenuSimple="matMenu">
                @if (!notification.read) {
                  <button
                    mat-menu-item
                    (click)="markAsRead(notification, $event)" (keydown.enter)="markAsRead(notification, $any($event))">
                    <mat-icon>done</mat-icon>
                    <span>{{ 'shared.notifications.list.markReadAction' | translate }}</span>
                  </button>
                }
                <button
                  mat-menu-item
                  (click)="removeNotification(notification, $event)" (keydown.enter)="removeNotification(notification, $any($event))">
                  <mat-icon>delete</mat-icon>
                  <span>{{ 'shared.notifications.list.removeAction' | translate }}</span>
                </button>
              </mat-menu>
            </div>
          }
        </div>
      }
      <!-- Empty state for simple list -->
      @if ((notifications$ | async)?.length === 0) {
        <div class="empty-state">
          <div class="empty-content">
            <mat-icon class="empty-icon">notifications_none</mat-icon>
            <h4>{{ 'shared.notifications.list.emptyTitle' | translate }}</h4>
            <p>{{ 'shared.notifications.list.emptyMsg' | translate }}</p>
            <button
              mat-raised-button
              color="primary"
              (click)="createTestNotifications()" (keydown.enter)="createTestNotifications()">
              {{ 'shared.notifications.list.createTestBtn' | translate }}
            </button>
          </div>
        </div>
      }
    </div>
  }

  <!-- Simple list (no grouping) -->

  <!-- Loading template -->

</div>